import { useState, useRef, useEffect } from 'react'
import { useMusic } from '../../contexts/MusicContext'
import { Play, Pause, SkipBack, SkipForward, SpeakerHigh, SpeakerLow, SpeakerSlash } from 'phosphor-react'
import Slider from '../Slider/Slider'
import styles from './MiniMusicPlayer.module.css'

export default function MiniMusicPlayer() {
  const { isPlaying, track, togglePlayPause, nextTrack, prevTrack, isReady, musicVolume, setMusicVolume } = useMusic()
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [isTitleLong, setIsTitleLong] = useState(false)
  const titleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (titleRef.current) {
      const titleWidth = titleRef.current.scrollWidth
      const containerWidth = titleRef.current.offsetWidth
      setIsTitleLong(titleWidth > containerWidth)
    }
  }, [track.title])

  if (!isReady) return null

  const getVolumeIcon = () => {
    if (musicVolume === 0) return <SpeakerSlash size={16} weight="fill" />
    if (musicVolume < 50) return <SpeakerLow size={16} weight="fill" />
    return <SpeakerHigh size={16} weight="fill" />
  }

  const toggleMute = () => {
    if (musicVolume > 0) {
      sessionStorage.setItem('previousMusicVolume', musicVolume.toString())
      setMusicVolume(0)
    } else {
      const previousVolume = sessionStorage.getItem('previousMusicVolume')
      setMusicVolume(previousVolume ? Number(previousVolume) : 50)
    }
  }

  return (
    <div className={styles.miniPlayer}>
      <div className={styles.albumArt}>
        <img src={track.thumbnail} alt={track.title} />
      </div>
      
      <div className={styles.info}>
        <div 
          ref={titleRef}
          className={`${styles.trackTitle} ${!isTitleLong ? styles.trackTitleShort : ''}`}
        >
          <span className={styles.trackTitleInner}>
            {track.title}
            {isTitleLong && ` • ${track.title}`}
          </span>
        </div>
        <div className={styles.artistName}>{track.artist}</div>
      </div>

      <div className={styles.controls}>
        <button 
          onClick={(e) => { e.stopPropagation(); prevTrack() }} 
          className={styles.controlButton}
          aria-label="Previous track"
          disabled={!isReady}
        >
          <SkipBack size={14} weight="fill" />
        </button>

        <button 
          onClick={(e) => { e.stopPropagation(); togglePlayPause() }} 
          className={styles.playButton}
          aria-label={isPlaying ? "Pause" : "Play"}
          disabled={!isReady}
        >
          {isPlaying ? (
            <Pause size={16} weight="fill" />
          ) : (
            <Play size={16} weight="fill" />
          )}
        </button>

        <button 
          onClick={(e) => { e.stopPropagation(); nextTrack() }} 
          className={styles.controlButton}
          aria-label="Next track"
          disabled={!isReady}
        >
          <SkipForward size={14} weight="fill" />
        </button>
      </div>

      <div className={styles.volumeControl}>
        <button
          className={styles.volumeButton}
          onClick={(e) => {
            e.stopPropagation()
            if (e.detail === 2) {
              toggleMute()
            } else {
              setShowVolumeSlider(!showVolumeSlider)
            }
          }}
          onMouseEnter={() => setShowVolumeSlider(true)}
          aria-label="Volume"
          title={`Music Volume: ${musicVolume}%`}
        >
          {getVolumeIcon()}
        </button>

        {showVolumeSlider && (
          <div 
            className={styles.volumeSliderContainer}
            onMouseLeave={() => setShowVolumeSlider(false)}
            onClick={(e) => e.stopPropagation()}
          >
            <Slider
              value={musicVolume}
              onChange={setMusicVolume}
              fillColor="rgba(0, 0, 0, 0.3)"
              thumbColor="#ffffff"
              trackColor="rgba(0, 0, 0, 0.06)"
            />
            <span className={styles.volumePercent}>{musicVolume}%</span>
          </div>
        )}
      </div>
    </div>
  )
}